import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  CurrencyDollarIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import {
  getReportsDashboard,
  getReportsSummary,
  getSalesReport,
  getDailyTotals,
} from '../../../services/reportsService';
import { getJobs } from '../../../services/jobsService';
import { getCustomers } from '../../../services/customersService';

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dailySalesData, setDailySalesData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
    return local.toISOString().split('T')[0];
  });
  const [dailyTotalsData, setDailyTotalsData] = useState(null);
  const [financialPeriod, setFinancialPeriod] = useState('monthly');
  const [financialData, setFinancialData] = useState(null);
  const [loadingFinancial, setLoadingFinancial] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedQuarter, setSelectedQuarter] = useState(Math.floor(new Date().getMonth() / 3) + 1);
  const [currentMonthData, setCurrentMonthData] = useState(null);
  const [recentJobsPage, setRecentJobsPage] = useState(1);
  const [topCustomersPage, setTopCustomersPage] = useState(1);
  const [recentJobsTotal, setRecentJobsTotal] = useState(0);
  const [topCustomersTotal, setTopCustomersTotal] = useState(0);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      fetchDailyTotals(selectedDate);
    }
  }, [selectedDate]);

  const fetchFinancialData = useCallback(async () => {
    try {
      setLoadingFinancial(true);
      let startDate, endDate;
      
      // Use current state values
      const currentPeriod = financialPeriod;
      const currentYear = selectedYear;
      const currentMonth = selectedMonth;
      const currentQuarter = selectedQuarter;
      
      switch (currentPeriod) {
        case 'monthly':
          startDate = new Date(currentYear, currentMonth, 1);
          endDate = new Date(currentYear, currentMonth + 1, 0);
          break;
        case 'quarterly':
          startDate = new Date(currentYear, (currentQuarter - 1) * 3, 1);
          endDate = new Date(currentYear, currentQuarter * 3, 0);
          break;
        case 'yearly':
          startDate = new Date(currentYear, 0, 1);
          endDate = new Date(currentYear, 11, 31);
          break;
        default:
          startDate = new Date(currentYear, currentMonth, 1);
          endDate = new Date(currentYear, currentMonth + 1, 0);
      }
      
      const response = await getReportsSummary({
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0]
      });

      setFinancialData(response);
    } catch (error) {
      console.error('Error fetching financial data:', error);
      setFinancialData(null);
    } finally {
      setLoadingFinancial(false);
    }
  }, [financialPeriod, selectedMonth, selectedYear, selectedQuarter]);

  useEffect(() => {
    fetchFinancialData();
  }, [fetchFinancialData]);

  const fetchDashboardData = async () => {
    try {
      const dashboardResponse = await getReportsDashboard();
      const jobsResponse = await getJobs({ limit: 1000 });
      const recentJobsResponse = await getJobs({ page: 1, limit: 5 });
      const topCustomersResponse = await getCustomers({ page: 1, limit: 5 });

      const combinedData = {
        ...dashboardResponse,
        recentJobs: jobsResponse.jobs || [],
        recentJobsPaginated: recentJobsResponse.jobs || [],
        topCustomers: topCustomersResponse.customers || [],
        totalCustomers: topCustomersResponse.total || 0
      };

      setDashboardData(combinedData);
      setRecentJobsTotal(recentJobsResponse.total || 0);
      setTopCustomersTotal(topCustomersResponse.total || 0);

      await fetchCurrentMonthData();
      await fetchDailySalesData();
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDailySalesData = async () => {
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - 6);

      const toLocalYmd = (d) => {
        const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
        return local.toISOString().split('T')[0];
      };

      const response = await getSalesReport({
        startDate: toLocalYmd(startDate),
        endDate: toLocalYmd(endDate),
        groupBy: 'day',
        basis: 'created',
      });

      const byDate = {};
      (response.salesData || []).forEach((item) => {
        byDate[item._id] = item.totalSales || 0;
      });

      const filled = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const key = toLocalYmd(date);
        filled.push({
          date: key,
          sales: byDate[key] || 0
        });
      }
      setDailySalesData(filled);
    } catch (error) {
      console.error('Error fetching daily sales data:', error);
      setDailySalesData([]);
    }
  };

  const fetchDailyTotals = async (date) => {
    try {
      setDailyTotalsData(null);

      const dailyData = await getDailyTotals({ date });

      setDailyTotalsData({
        dailyTotals: {
          totalSales: dailyData?.dailyTotals?.totalSales || 0,
          totalTillCollected: dailyData?.dailyTotals?.totalTillCollected || 0,
          totalCashCollected: dailyData?.dailyTotals?.totalCashCollected || 0,
        },
        pendingSummary: {
          totalBalance: dailyData?.pendingSummary?.totalBalance || 0,
          totalPendingJobs: dailyData?.pendingSummary?.totalPendingJobs || 0,
        },
        totalJobs: dailyData?.totalJobs || 0
      });
    } catch (error) {
      console.error('Error fetching daily totals:', error);
      setDailyTotalsData({
        dailyTotals: {
          totalSales: 0,
          totalTillCollected: 0,
          totalCashCollected: 0,
        },
        pendingSummary: {
          totalBalance: 0,
          totalPendingJobs: 0,
        },
        totalJobs: 0
      });
    }
  };


  const getPeriodLabel = (period) => {
    // Use current state values directly to ensure they're up to date
    const currentMonth = selectedMonth;
    const currentYear = selectedYear;
    const currentQuarter = selectedQuarter;
    
    switch (period) {
      case 'monthly':
        return new Date(currentYear, currentMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      case 'quarterly':
        return `Q${currentQuarter} ${currentYear}`;
      case 'yearly':
        return currentYear.toString();
      default:
        return '';
    }
  };

  const calculateOverdueJobs = (jobs) => {
    if (!jobs || !Array.isArray(jobs)) return 0;
    const today = new Date();
    return jobs.filter(job => {
      if (!job.expectedDueDate) return false;
      const dueDate = new Date(job.expectedDueDate);
      return dueDate < today && job.status !== 'collected';
    }).length;
  };

  const calculatePendingJobs = (jobs) => {
    if (!jobs || !Array.isArray(jobs)) return 0;
    return jobs.filter(job => job.status !== 'collected').length;
  };

  const fetchRecentJobs = async (page) => {
    try {
      const response = await getJobs({ page, limit: 5 });
      setDashboardData(prev => ({
        ...prev,
        recentJobsPaginated: response.jobs || []
      }));
      setRecentJobsPage(page);
    } catch (error) {
      console.error('Error fetching recent jobs:', error);
    }
  };

  const fetchTopCustomers = async (page) => {
    try {
      const response = await getCustomers({ page, limit: 5 });
      setDashboardData(prev => ({
        ...prev,
        topCustomers: response.customers || []
      }));
      setTopCustomersPage(page);
    } catch (error) {
      console.error('Error fetching top customers:', error);
    }
  };

  const fetchCurrentMonthData = async () => {
    try {
      const startDate = new Date(selectedYear, selectedMonth, 1);
      const endDate = new Date(selectedYear, selectedMonth + 1, 0);

      const response = await getReportsSummary({
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0]
      });

      setCurrentMonthData(response);
    } catch (error) {
      console.error('Error fetching current month data:', error);
      setCurrentMonthData(null);
    }
  };

  // Auto-refresh current month data when month changes
  useEffect(() => {
    const checkMonthChange = () => {
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth();
      const currentYear = currentDate.getFullYear();
      
      // If month or year changed, refresh current month data
      if (currentMonth !== selectedMonth || currentYear !== selectedYear) {
        setSelectedMonth(currentMonth);
        setSelectedYear(currentYear);
        fetchCurrentMonthData();
      }
    };

    // Check every hour for month changes
    const interval = setInterval(checkMonthChange, 60 * 60 * 1000);
    
    // Initial check
    checkMonthChange();
    
    return () => clearInterval(interval);
  }, [selectedMonth, selectedYear]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="text-center text-gray-500">
        Failed to load dashboard data
      </div>
    );
  }

  const jobStatusData = dashboardData.jobStats?.map(stat => ({
    name: stat._id.charAt(0).toUpperCase() + stat._id.slice(1),
    value: stat.count,
  })) || [];

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  return (
    <div className="space-y-6">
      {/* Financial Period Selector */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Financial Overview</h3>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">Period:</span>
            <div className="flex bg-gray-100 rounded-lg p-1">
              {['monthly', 'quarterly', 'yearly'].map((period) => (
                <button
                  key={period}
                  onClick={() => setFinancialPeriod(period)}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                    financialPeriod === period
                      ? 'bg-white text-primary-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {period.charAt(0).toUpperCase() + period.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Period-Specific Selectors */}
        <div className="flex items-center gap-4">
          {financialPeriod === 'monthly' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => {
                    const newMonth = parseInt(e.target.value);
                    setSelectedMonth(newMonth);
                  }}
                  className="input-field"
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i} value={i}>
                      {new Date(2024, i).toLocaleDateString('en-US', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="input-field"
                >
                  {Array.from({ length: 5 }, (_, i) => {
                    const year = new Date().getFullYear() - 2 + i;
                    return (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    );
                  })}
                </select>
              </div>
            </>
          )}
          
          {financialPeriod === 'quarterly' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quarter</label>
                <select
                  value={selectedQuarter}
                  onChange={(e) => setSelectedQuarter(parseInt(e.target.value))}
                  className="input-field"
                >
                  <option value={1}>Q1 (Jan-Mar)</option>
                  <option value={2}>Q2 (Apr-Jun)</option>
                  <option value={3}>Q3 (Jul-Sep)</option>
                  <option value={4}>Q4 (Oct-Dec)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="input-field"
                >
                  {Array.from({ length: 5 }, (_, i) => {
                    const year = new Date().getFullYear() - 2 + i;
                    return (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    );
                  })}
                </select>
              </div>
            </>
          )}
          
          {financialPeriod === 'yearly' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="input-field"
              >
                {Array.from({ length: 5 }, (_, i) => {
                  const year = new Date().getFullYear() - 2 + i;
                  return (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  );
                })}
              </select>
            </div>
          )}
        </div>
      </div>

       {/* All-Time Business Overview */}
       <div className="card p-4">
         <div className="flex items-center justify-between mb-4">
           <h4 className="text-md font-medium text-gray-900">All-Time Business Overview</h4>
           <span className="text-sm text-gray-500">Complete business performance since start</span>
         </div>
         <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
           <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl shadow-md border border-blue-200">
             <div className="text-sm text-blue-700 font-medium">Total Revenue</div>
             <div className="text-2xl font-bold text-blue-900 mt-1">
               KSh {(dashboardData.totalRevenue || 0).toLocaleString('en-KE')}
             </div>
           </div>
           <div className="p-4 bg-gradient-to-br from-red-50 to-pink-100 rounded-xl shadow-md border border-red-200">
             <div className="text-sm text-red-700 font-medium">Total Expenses</div>
             <div className="text-2xl font-bold text-red-900 mt-1">
               KSh {(dashboardData.totalExpenses || 0).toLocaleString('en-KE')}
             </div>
           </div>
           <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl shadow-md border border-green-200">
             <div className="text-sm text-green-700 font-medium">Total Net Profit</div>
             <div className="text-2xl font-bold text-green-900 mt-1">
               KSh {(dashboardData.netProfit || 0).toLocaleString('en-KE')}
             </div>
           </div>
         </div>
       </div>



      {/* Period-Specific Financial Data */}
      {(financialData || loadingFinancial) && (
        <div className="card p-4" key={`financial-${financialPeriod}-${selectedMonth}-${selectedYear}-${selectedQuarter}`}>
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-md font-medium text-gray-900">
              {loadingFinancial ? 'Loading...' : `${getPeriodLabel(financialPeriod)} Financial Summary`}
            </h4>
            <span className="text-sm text-gray-500">
              {loadingFinancial ? 'Fetching data...' : 'Filtered data for selected period'}
            </span>
          </div>
          {loadingFinancial ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : financialData && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl shadow-md border border-blue-200">
              <div className="text-sm text-blue-700 font-medium">Period Sales</div>
              <div className="text-2xl font-bold text-blue-900 mt-1">
                KSh {(financialData.salesTotal || 0).toLocaleString('en-KE')}
              </div>
            </div>
            <div className="p-4 bg-gradient-to-br from-red-50 to-pink-100 rounded-xl shadow-md border border-red-200">
              <div className="text-sm text-red-700 font-medium">Period Expenses</div>
              <div className="text-2xl font-bold text-red-900 mt-1">
                KSh {(financialData.expensesTotal || 0).toLocaleString('en-KE')}
              </div>
            </div>
            <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl shadow-md border border-green-200">
              <div className="text-sm text-green-700 font-medium">Period Net</div>
              <div className="text-2xl font-bold text-green-900 mt-1">
                KSh {((financialData.salesTotal || 0) - (financialData.expensesTotal || 0)).toLocaleString('en-KE')}
              </div>
            </div>
          </div>
          )}
        </div>
      )}

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Totals & Pending */}
        <div className="relative overflow-hidden rounded-2xl shadow-lg border border-orange-200 bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-orange-900">Daily Totals</h3>
                <p className="text-sm text-orange-600">Track daily performance</p>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-orange-700">Date:</label>
                <input 
                  type="date" 
                  value={selectedDate} 
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-orange-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent shadow-sm"
                />
              </div>
            </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-100 p-4 rounded-xl shadow-md border border-blue-200">
              <div className="text-sm text-blue-700 font-medium">Total Sales</div>
              <div className="text-2xl font-bold text-blue-900 mt-1">
                {dailyTotalsData ? (
                  `KSh ${Math.round(dailyTotalsData.dailyTotals?.totalSales || 0).toLocaleString('en-KE')}`
                ) : (
                  <span className="text-gray-400">Loading...</span>
                )}
              </div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-emerald-100 p-4 rounded-xl shadow-md border border-green-200">
              <div className="text-sm text-green-700 font-medium">Till Collected</div>
              <div className="text-2xl font-bold text-green-900 mt-1">
                {dailyTotalsData ? (
                  `KSh ${Math.round(dailyTotalsData.dailyTotals?.totalTillCollected || 0).toLocaleString('en-KE')}`
                ) : (
                  <span className="text-gray-400">Loading...</span>
                )}
              </div>
            </div>
            <div className="bg-gradient-to-br from-yellow-50 to-amber-100 p-4 rounded-xl shadow-md border border-yellow-200">
              <div className="text-sm text-yellow-700 font-medium">Cash Collected</div>
              <div className="text-2xl font-bold text-yellow-900 mt-1">
                {dailyTotalsData ? (
                  `KSh ${Math.round(dailyTotalsData.dailyTotals?.totalCashCollected || 0).toLocaleString('en-KE')}`
                ) : (
                  <span className="text-gray-400">Loading...</span>
                )}
              </div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-violet-100 p-4 rounded-xl shadow-md border border-purple-200">
              <div className="text-sm text-purple-700 font-medium">Pending Balance</div>
              <div className="text-2xl font-bold text-purple-900 mt-1">
                {dailyTotalsData ? (
                  `KSh ${Math.round(dailyTotalsData.pendingSummary?.totalBalance || 0).toLocaleString('en-KE')}`
                ) : (
                  <span className="text-gray-400">Loading...</span>
                )}
              </div>
            </div>
          </div>
          <div className="text-sm text-orange-700 bg-white rounded-lg p-3 shadow-sm">
            <span className="font-semibold">Pending Jobs:</span> <span className="text-gray-900 font-bold">
              {dailyTotalsData ? (dailyTotalsData.pendingSummary?.totalPendingJobs || 0) : (
                <span className="text-gray-400">Loading...</span>
              )}
            </span>
            {dailyTotalsData && dailyTotalsData.totalJobs !== undefined && (
              <span className="ml-4 text-gray-600">
                | Total Jobs: <span className="font-bold">{dailyTotalsData.totalJobs}</span>
              </span>
            )}
          </div>
          </div>
        </div>

        {/* Job Status Chart */}
        <div className="relative overflow-hidden rounded-2xl shadow-lg border border-purple-200 bg-gradient-to-br from-purple-50 via-violet-50 to-fuchsia-50">
          <div className="p-6">
            <div>
              <h3 className="text-lg font-bold text-purple-900 mb-1">Job Status Distribution</h3>
              <p className="text-sm text-purple-600 mb-4">Current jobs breakdown</p>
            </div>
            <div className="h-64 bg-white rounded-xl p-4 shadow-inner">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={jobStatusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {jobStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: '#faf5ff', border: '1px solid #d8b4fe', borderRadius: '8px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Daily Sales Trend Chart */}
        <div className="relative overflow-hidden rounded-2xl shadow-lg border border-green-200 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-green-900">Daily Sales Trend</h3>
                <p className="text-sm text-green-600">Last 7 Days Performance</p>
              </div>
              <button 
                onClick={fetchDailySalesData}
                className="px-3 py-1.5 text-sm font-medium text-green-700 bg-white rounded-lg hover:bg-green-50 shadow-sm border border-green-200 transition-all"
              >
                Refresh
              </button>
            </div>
            <div className="h-64 bg-white rounded-xl p-4 shadow-inner">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dailySalesData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#d1fae5" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    stroke="#059669"
                  />
                  <YAxis 
                    tickFormatter={(value) => `KSh ${value.toFixed(0)}`}
                    stroke="#059669"
                  />
                  <Tooltip 
                    formatter={(value) => [`KSh ${value.toFixed(2)}`, 'Sales']}
                    labelFormatter={(value) => new Date(value).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                    contentStyle={{ backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="sales" 
                    stroke="#10b981" 
                    strokeWidth={3}
                    dot={{ fill: '#10b981', strokeWidth: 2, r: 5 }}
                    activeDot={{ r: 7, stroke: '#059669', strokeWidth: 2, fill: '#34d399' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

                 {/* Recent Jobs */}
        <div className="relative overflow-hidden rounded-2xl shadow-lg border border-indigo-200 bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-indigo-900">Recent Jobs</h3>
                <p className="text-sm text-indigo-600">Latest job orders</p>
              </div>
              <Link to="/jobs" className="px-3 py-1.5 text-sm font-medium text-indigo-700 bg-white rounded-lg hover:bg-indigo-50 shadow-sm border border-indigo-200 transition-all">
                View all
              </Link>
            </div>
            <div className="space-y-3">
               {dashboardData.recentJobsPaginated?.map((job) => (
                <div key={job._id} className="flex items-center justify-between p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all border border-indigo-100">
                  <div>
                    <p className="text-sm font-bold text-indigo-900">{job.jobId}</p>
                    <p className="text-sm text-gray-600">{job.customer?.name}</p>
                  </div>
                  <div className="text-right">
                     <p className="text-sm font-bold text-indigo-900">KSh {job.totalPrice?.toFixed(2)}</p>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold shadow-sm ${
                      job.status === 'design' ? 'bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-800 border border-yellow-300' :
                      job.status === 'approved' ? 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 border border-blue-300' :
                      job.status === 'printed' ? 'bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 border border-purple-300' :
                      job.status === 'ready_for_collection' ? 'bg-gradient-to-r from-green-100 to-green-200 text-green-800 border border-green-300' :
                      'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 border border-gray-300'
                    }`}>
                      {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                    </span>
                  </div>
                </div>
              )) || (
                <p className="text-gray-500 text-center py-4">No recent jobs</p>
              )}
            </div>
           
           {/* Recent Jobs Pagination */}
           {recentJobsTotal > 5 && (
             <div className="mt-4 flex items-center justify-between">
               <div className="text-sm text-gray-500">
                 Showing {((recentJobsPage - 1) * 5) + 1} to {Math.min(recentJobsPage * 5, recentJobsTotal)} of {recentJobsTotal} jobs
               </div>
               <div className="flex items-center gap-2">
                 <button
                   onClick={() => fetchRecentJobs(recentJobsPage - 1)}
                   disabled={recentJobsPage === 1}
                   className={`px-3 py-1 text-sm rounded-md ${
                     recentJobsPage === 1
                       ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                       : 'bg-primary-100 text-primary-600 hover:bg-primary-200'
                   }`}
                 >
                   Previous
                 </button>
                 <span className="text-sm text-gray-600">
                   Page {recentJobsPage} of {Math.ceil(recentJobsTotal / 5)}
                 </span>
                 <button
                   onClick={() => fetchRecentJobs(recentJobsPage + 1)}
                   disabled={recentJobsPage >= Math.ceil(recentJobsTotal / 5)}
                   className={`px-3 py-1 text-sm rounded-md ${
                     recentJobsPage >= Math.ceil(recentJobsTotal / 5)
                       ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                       : 'bg-primary-100 text-primary-600 hover:bg-primary-200'
                   }`}
                 >
                   Next
                 </button>
               </div>
             </div>
           )}
          </div>
        </div>
      </div>

      {/* Top Customers */}
      <div className="relative overflow-hidden rounded-2xl shadow-lg border border-cyan-200 bg-gradient-to-br from-cyan-50 via-sky-50 to-blue-50">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-cyan-900">Top Customers</h3>
              <p className="text-sm text-cyan-600">Most valuable clients</p>
            </div>
            <Link to="/customers" className="px-3 py-1.5 text-sm font-medium text-cyan-700 bg-white rounded-lg hover:bg-cyan-50 shadow-sm border border-cyan-200 transition-all">
              View all
            </Link>
          </div>
          <div className="overflow-x-auto bg-white rounded-xl shadow-inner">
            <table className="min-w-full divide-y divide-cyan-100">
              <thead className="bg-gradient-to-r from-cyan-50 to-sky-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold text-cyan-800 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-cyan-800 uppercase tracking-wider">
                    Total Jobs
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-cyan-800 uppercase tracking-wider">
                    Total Spent
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {dashboardData.topCustomers?.map((customer, index) => (
                  <tr key={customer._id} className={`hover:bg-cyan-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                      {customer.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-cyan-100 text-cyan-800">
                        {customer.totalJobs} jobs
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-cyan-900">
                       KSh {customer.totalSpent?.toFixed(2)}
                    </td>
                  </tr>
                )) || (
                  <tr>
                    <td colSpan="3" className="px-6 py-4 text-center text-gray-500">
                      No customers found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
         
         {/* Top Customers Pagination */}
         {topCustomersTotal > 5 && (
           <div className="mt-4 flex items-center justify-between">
             <div className="text-sm text-gray-500">
               Showing {((topCustomersPage - 1) * 5) + 1} to {Math.min(topCustomersPage * 5, topCustomersTotal)} of {topCustomersTotal} customers
             </div>
             <div className="flex items-center gap-2">
               <button
                 onClick={() => fetchTopCustomers(topCustomersPage - 1)}
                 disabled={topCustomersPage === 1}
                 className={`px-3 py-1 text-sm rounded-md ${
                   topCustomersPage === 1
                     ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                     : 'bg-primary-100 text-primary-600 hover:bg-primary-200'
                 }`}
               >
                 Previous
               </button>
               <span className="text-sm text-gray-600">
                 Page {topCustomersPage} of {Math.ceil(topCustomersTotal / 5)}
               </span>
               <button
                 onClick={() => fetchTopCustomers(topCustomersPage + 1)}
                 disabled={topCustomersPage >= Math.ceil(topCustomersTotal / 5)}
                 className={`px-3 py-1 text-sm rounded-md ${
                   topCustomersPage >= Math.ceil(topCustomersTotal / 5)
                     ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                     : 'bg-primary-100 text-primary-600 hover:bg-primary-200'
                 }`}
               >
                 Next
               </button>
             </div>
           </div>
         )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 